import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Overline from "../components/Overline";
import IconButton from "../components/IconButton";
import ProgressBar from "../components/ProgressBar";
import { PlayIcon, PauseIcon } from "../components/icons";
import { usePlayer } from "../player/PlayerProvider";
import { useSelector } from "react-redux";
import { paths } from "../routing/paths";
import { getContinueItems } from "../api/redesignApi";
import { ragSimilar } from "../../service/ragSearch";

/*
 * Zone 2 (Homepage reference):
 * signed-in → "Continue" rail: navy resume card (Hebrew-lockup art, series
 *   chip, progress + time-left, 56px resume) + light recently-played cards.
 *   Series-aware "Up next · Class N of M" arrives with the Phase 6 progress
 *   service (BACKEND-TODO.md).
 * signed-out → compact single-row navy hero, intentionally short so
 *   Coming Up stays above the fold.
 */

function ResumeCard({ item }) {
  const player = usePlayer();
  const current = useSelector((s) => s.player.current);
  const playing = useSelector((s) => s.player.playing);
  const position = useSelector((s) => s.player.position);
  const duration = useSelector((s) => s.player.duration);

  const isCurrent = current?.src === item.src;
  const pos = isCurrent ? position : item.positionSeconds;
  const dur = isCurrent && duration > 0 ? duration : item.durationSeconds || 0;
  const pct = dur > 0 ? (pos / dur) * 100 : item.positionSeconds > 0 ? 15 : 0;
  const remaining = dur > 0 ? Math.max(0, Math.round((dur - pos) / 60)) : null;

  const onClick = () => {
    if (isCurrent) player.toggle();
    else player.load({ title: item.title, mediaKind: "audio", src: item.src }, { autoplay: true });
  };

  return (
    <div
      className="bg-ic-navy border border-white/10 rounded-xl shadow-ic-raised-dark p-5 flex items-center gap-4 transition-all duration-150 ease-out hover:shadow-ic-elevated-dark"
      style={{ flex: "0 0 clamp(300px, 84vw, 560px)" }}
    >
      <div className="flex-shrink-0 w-[88px] h-[88px] rounded-xl bg-ic-navyCard hidden xs:flex flex-col items-center justify-center gap-0.5">
        <span dir="rtl" className="text-[26px] font-extrabold text-ic-sky leading-8">שיעור</span>
        <span className="text-[10px] font-bold tracking-[0.5px] text-ic-lightGray uppercase">Continue</span>
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <span className="self-start bg-white/10 text-ic-lightGray text-[12px] font-semibold tracking-[0.5px] px-2.5 py-0.5 rounded-xl">
          Recently played
        </span>
        <span className="text-[18px] font-semibold leading-7 text-white ic-wrap-anywhere">{item.title}</span>
        <div className="flex items-center gap-2.5">
          <div className="flex-1">
            <ProgressBar value={pct} theme="dark" height={4} />
          </div>
          {remaining !== null && (
            <span className="text-[12px] leading-4 text-ic-lightGray flex-shrink-0">{remaining} min left</span>
          )}
        </div>
      </div>
      <IconButton
        variant="solid"
        dark
        size={56}
        label={`Resume ${item.title}`}
        onClick={onClick}
      >
        {isCurrent && playing ? <PauseIcon size={22} /> : <PlayIcon size={22} />}
      </IconButton>
    </div>
  );
}

function RecentCard({ item }) {
  const player = usePlayer();
  return (
    <div
      className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised p-5 flex items-center gap-4 transition-all duration-150 ease-out hover:shadow-ic-elevated hover:border-ic-borderGray"
      style={{ flex: "0 0 clamp(260px, 72vw, 400px)" }}
    >
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <Overline>Recently played</Overline>
        <span className="text-[18px] font-semibold leading-7 text-black ic-wrap-anywhere">{item.title}</span>
      </div>
      <IconButton
        variant="circleOutline"
        size={48}
        label={`Play ${item.title}`}
        onClick={() => player.load({ title: item.title, mediaKind: "audio", src: item.src }, { autoplay: true })}
      >
        <PlayIcon size={20} />
      </IconButton>
    </div>
  );
}

/* "Up next · Class N of M" — the reference's second card, powered by the
 * series position of the class being resumed (RAG /similar). */
function UpNextCard({ classId }) {
  const navigate = useNavigate();
  const [next, setNext] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setNext(null);
    if (classId) {
      ragSimilar(classId, 1)
        .then((r) => {
          if (!cancelled && r?.series?.next) {
            setNext({
              title: r.series.next.title,
              href: paths.permalink(r.series.next.permalink || r.series.next.class_id),
              label: `Up next · Class ${r.series.position + 1} of ${r.series.total}`,
              meta: r.series.title,
            });
          }
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [classId]);

  if (!next) return null;
  return (
    <div
      className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised p-5 flex items-center gap-4 transition-all duration-150 ease-out hover:shadow-ic-elevated hover:border-ic-borderGray"
      style={{ flex: "0 0 clamp(260px, 72vw, 400px)" }}
    >
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <Overline>{next.label}</Overline>
        <span className="text-[18px] font-semibold leading-7 text-black ic-wrap-anywhere">{next.title}</span>
        <span className="text-[14px] leading-5 text-ic-slate ic-wrap-anywhere">{next.meta}</span>
      </div>
      <IconButton variant="circleOutline" size={48} label={`Play next: ${next.title}`} onClick={() => navigate(next.href)}>
        <PlayIcon size={20} />
      </IconButton>
    </div>
  );
}

export default function ContinueZone() {
  const navigate = useNavigate();
  const signedIn = Boolean(localStorage.getItem("accessToken"));
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (signedIn) getContinueItems().then(setItems);
  }, [signedIn]);

  if (!signedIn) {
    return (
      <section className="max-w-[1280px] mx-auto px-5 pt-6 w-full">
        <div className="bg-ic-navy rounded-ic-hero px-5 sm:px-8 py-4 sm:py-6 flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px] flex flex-col gap-1">
            <h1 className="m-0 font-extrabold text-white tracking-[-0.4px]" style={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.25 }}>
              15,000+ classes on Chassidus
            </h1>
            <p className="m-0 text-[16px] font-normal leading-6 text-ic-offwhite max-w-[560px]">
              Rabbi Yossi Paltiel's complete library: Tanya, Ma'amarim, Sichos, Tefilla, the Chassidic
              calendar and more.
            </p>
          </div>
          <div className="flex gap-3 flex-wrap flex-shrink-0">
            <button
              type="button"
              onClick={() => navigate(paths.categories())}
              className="bg-ic-blue text-ic-offwhite font-sans text-[16px] font-bold leading-6 px-7 h-12 border-0 rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:bg-ic-blueHover hover:shadow-ic-cta-hover active:bg-ic-blueActive active:shadow-ic-inset focus-visible:outline-none focus-visible:shadow-ic-focus"
            >
              Start here
            </button>
            <button
              type="button"
              onClick={() => navigate(paths.signIn())}
              className="bg-transparent text-ic-offwhite font-sans text-[16px] font-semibold leading-6 px-5 h-12 border border-ic-lightGray rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:border-ic-sky hover:text-ic-sky focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
            >
              Sign in
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (!items || items.length === 0) return null;

  const [first, ...rest] = items;
  return (
    <section className="max-w-[1280px] mx-auto px-5 pt-6 w-full flex flex-col gap-3">
      <Overline>Continue</Overline>
      <div className="ic-rail flex gap-4 overflow-x-auto pb-2 items-stretch">
        <ResumeCard item={first} />
        <UpNextCard classId={first.classId} />
        {rest.slice(0, 2).map((item) => (
          <RecentCard key={item.id} item={item} />
        ))}
      </div>
    </section>
  );
}
